import { useCallback, useEffect, useMemo, useState } from "react";
import { isAutoGeneratedConstraintXmlName } from "../../utils/constraintNames";
import { stripNamespacePrefix as stripInspectorPrefix } from "../../utils/nameUtils";

export function useInspectorTitleEditing({
  activePanel,
  resolvedSelectedGeom,
  sceneActions,
  selectedActuator,
  selectedActuatorRecord,
  selectedBody,
  selectedBodyExists,
  selectedBodyName,
  selectedCamera,
  selectedCameraRecord,
  selectedEquality,
  selectedEqualityRecord,
  selectedInertial,
  selectedInertialBodyName,
  selectedJoint,
  selectedJointRecord,
  selectedLight,
  selectedLightRecord,
  selectedSite,
  selectedSiteRecord,
  selectedTendon,
  selectedTendonRecord,
  selectedWorldBodyDisplayName,
}) {
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [titleDraftName, setTitleDraftName] = useState("");

  const renameTarget = useMemo(() => {
    switch (activePanel) {
      case "camera": {
        if (!selectedCamera) return null;
        const camera = selectedCameraRecord;
        if (!camera) return null;
        return {
          key: `camera:${selectedCamera}`,
          currentName: camera.name || "",
          applyName: (nextName) => sceneActions.updateCamera(selectedCamera, { name: nextName }),
        };
      }
      case "light": {
        if (!selectedLight) return null;
        const light = selectedLightRecord;
        if (!light) return null;
        return {
          key: `light:${selectedLight}`,
          currentName: light.name || "",
          applyName: (nextName) => sceneActions.updateLight(selectedLight, { name: nextName }),
        };
      }
      case "equality": {
        if (!selectedEquality) return null;
        const equality = selectedEqualityRecord;
        if (!equality) return null;
        return {
          key: `equality:${selectedEquality}`,
          currentName: !isAutoGeneratedConstraintXmlName(equality.name) ? equality.name || "" : "",
          applyName: (nextName) =>
            sceneActions.updateEquality(selectedEquality, { name: nextName }),
        };
      }
      case "tendon": {
        if (!selectedTendon) return null;
        const tendon = selectedTendonRecord;
        if (!tendon) return null;
        return {
          key: `tendon:${selectedTendon}`,
          currentName: tendon.name || "",
          applyName: (nextName) => sceneActions.updateTendon(selectedTendon, { name: nextName }),
        };
      }
      case "actuator": {
        if (!selectedActuator) return null;
        const actuator = selectedActuatorRecord;
        if (!actuator) return null;
        return {
          key: `actuator:${selectedActuator}`,
          currentName: actuator.name || "",
          applyName: (nextName) =>
            sceneActions.updateActuator(selectedActuator, { name: nextName }),
        };
      }
      case "joint": {
        if (!selectedJoint) return null;
        const joint = selectedJointRecord;
        if (!joint) return null;
        return {
          key: `joint:${selectedJoint}`,
          currentName: joint.name || "",
          applyName: (nextName) => sceneActions.setJointName(selectedJoint, nextName),
        };
      }
      case "body": {
        if (!selectedBody) return null;
        if (!selectedBodyExists) return null;
        return {
          key: `body:${selectedBody}`,
          currentName: selectedBodyName || "",
          applyName: (nextName) => sceneActions.setBodyName(selectedBody, nextName),
        };
      }
      case "object": {
        if (!selectedBody) return null;
        if (!selectedBodyExists) return null;
        const displayName =
          stripInspectorPrefix(selectedWorldBodyDisplayName || selectedBodyName || "") || "";
        return {
          key: `object:${selectedBody}`,
          currentName: displayName,
          applyName: (nextName) => sceneActions.setWorldBodyDisplayName(selectedBody, nextName),
        };
      }
      case "site": {
        if (!selectedSite) return null;
        const site = selectedSiteRecord;
        if (!site?.uuid) return null;
        return {
          key: `site:${site.uuid}`,
          currentName: site.name || "",
          applyName: (nextName) => sceneActions.setSiteName(site.uuid, nextName),
        };
      }
      case "geom":
      case "visualGeom": {
        const bodyUuid = resolvedSelectedGeom?.bodyUuid || null;
        const geom = resolvedSelectedGeom?.geom || null;
        const geomUuid = geom?.uuid ? String(geom.uuid) : null;
        if (!bodyUuid || !geomUuid || !geom) return null;
        return {
          key: `geom:${bodyUuid}:${geomUuid}`,
          currentName: geom.name || "",
          applyName: (nextName) => sceneActions.setGeomName(bodyUuid, geomUuid, nextName),
        };
      }
      case "inertial": {
        const str = String(selectedInertial || "");
        const sepIdx = str.indexOf(":");
        const bodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
        if (!bodyUuid) return null;
        return {
          key: `inertial:${bodyUuid}`,
          currentName: selectedInertialBodyName || "",
          applyName: (nextName) => sceneActions.setInertiaName(bodyUuid, nextName),
        };
      }
      default:
        return null;
    }
  }, [
    activePanel,
    resolvedSelectedGeom,
    sceneActions,
    selectedActuator,
    selectedActuatorRecord,
    selectedBody,
    selectedBodyExists,
    selectedBodyName,
    selectedCamera,
    selectedCameraRecord,
    selectedEquality,
    selectedEqualityRecord,
    selectedInertial,
    selectedInertialBodyName,
    selectedJoint,
    selectedJointRecord,
    selectedLight,
    selectedLightRecord,
    selectedSite,
    selectedSiteRecord,
    selectedTendon,
    selectedTendonRecord,
    selectedWorldBodyDisplayName,
  ]);

  useEffect(() => {
    setIsRenamingTitle(false);
    setTitleDraftName(renameTarget?.currentName || "");
  }, [renameTarget?.key, renameTarget?.currentName]);

  const commitTitleRename = useCallback(() => {
    if (!renameTarget) return;
    const trimmedName = titleDraftName.trim();
    if (!trimmedName) {
      setTitleDraftName(renameTarget.currentName || "");
      setIsRenamingTitle(false);
      return;
    }
    if (trimmedName !== renameTarget.currentName) {
      renameTarget.applyName(trimmedName);
    }
    setIsRenamingTitle(false);
  }, [renameTarget, titleDraftName]);

  const cancelTitleRename = useCallback(() => {
    setTitleDraftName(renameTarget?.currentName || "");
    setIsRenamingTitle(false);
  }, [renameTarget]);

  const startInspectorRename = useCallback(() => {
    if (!renameTarget || isRenamingTitle) return false;
    setTitleDraftName(renameTarget.currentName || "");
    setIsRenamingTitle(true);
    return true;
  }, [renameTarget, isRenamingTitle]);

  return {
    cancelTitleRename,
    commitTitleRename,
    isRenamingTitle,
    renameTarget,
    setTitleDraftName,
    startInspectorRename,
    titleDraftName,
  };
}
